/**
 * Common Zod schemas — Spec Bölüm 5.1 (soru metadata)
 */
import { z } from 'zod';

// ─── Enum'lar — Spec Bölüm 5.1 ──────────────────────────────────

/** Konu kategorileri — Spec Bölüm 5.1 Boyut 1 */
export const TopicSchema = z.enum([
  'PM', // Part Modeling
  'AS', // Assembly
  'DR', // Drawing
  'SK', // Sketch & Design Intent
  'SM', // Sheet Metal
  'WD', // Weldments
  'SF', // Surfacing
  'CF', // Configurations & Equations
  'GN', // General SW Knowledge
  'DT', // Design Theory
]);
export type Topic = z.infer<typeof TopicSchema>;

/** Zorluk bandı — Spec Bölüm 5.1 Boyut 2 */
export const DifficultySchema = z.enum(['L1', 'L2', 'L3']);
export type Difficulty = z.infer<typeof DifficultySchema>;

/** Soru formatı — Spec Bölüm 5.1 Boyut 3 */
export const QuestionFormatSchema = z.enum([
  'MC', // Multiple Choice
  'MC_NEG', // Negatif MC
  'IMG', // Image Identification
  'MASS', // Model & Calculate Mass
  'COM', // Assembly & Center of Mass
  'DRAW_ERR', // Drawing Error Detection
  'SEQ', // Feature Sequencing (V2)
]);
export type QuestionFormat = z.infer<typeof QuestionFormatSchema>;

/** Endüstri kategorileri — Spec Bölüm 11 + 12 */
export const IndustrySchema = z.enum([
  'MACHINERY',
  'AUTOMOTIVE',
  'DEFENSE',
  'CONSUMER',
  'SHEET_FAB',
  'PRECISION_MECHANICAL', // V2 hedefi
  'OTHER',
]);
export type Industry = z.infer<typeof IndustrySchema>;

/** Aday deneyim bandı — Spec Bölüm 6.1.1 (tonal kalibrasyon) */
export const ExperienceBandSchema = z.enum([
  'LT_1Y', // <1 yıl
  'Y1_TO_2', // 1-2 yıl
  'Y2_TO_5', // 2-5 yıl
  'GT_5Y', // 5+ yıl
]);
export type ExperienceBand = z.infer<typeof ExperienceBandSchema>;

/** Skor seviyesi — Spec Bölüm 10.1 */
export const ScoreLevelSchema = z.enum([
  'L0', // 0-29   Tanışma
  'L1_MINUS', // 30-44  Başlangıç
  'L1', // 45-59  CSWA seviyesi
  'L2', // 60-74  CSWA üzeri
  'L3', // 75-89  CSWP seviyesi
  'L4', // 90-100 İleri seviye
]);
export type ScoreLevel = z.infer<typeof ScoreLevelSchema>;

/** Organization role (RBAC) — Spec Bölüm 4.5.2 */
export const OrgRoleSchema = z.enum(['OWNER', 'ADMIN', 'VIEWER']);
export type OrgRole = z.infer<typeof OrgRoleSchema>;

/** Sınav denemesi durumu — Spec Bölüm 12 */
export const AttemptStatusSchema = z.enum([
  'IN_PROGRESS',
  'COMPLETED',
  'EXPIRED',
  'ABANDONED',
]);
export type AttemptStatus = z.infer<typeof AttemptStatusSchema>;

/** Near-miss kategorisi — Spec Bölüm 5.5.1 */
export const NearMissCategorySchema = z.enum([
  'exact', // ≤ %1 sapma — tam puan
  'near_miss', // %1-3 sapma — yarım puan
  'close_wrong', // %3-10 sapma — çeyrek puan
  'wrong', // > %10 sapma — sıfır
]);
export type NearMissCategory = z.infer<typeof NearMissCategorySchema>;

/** Lead status (CRM) — Spec Bölüm 9.2 + 12 */
export const LeadStatusSchema = z.enum([
  'NEW',
  'FOUNDER_MEMBER',
  'EXAM_CREATED',
  'EXAM_IN_PROGRESS',
  'EXAM_COMPLETED',
  'REPORT_DOWNLOADED',
  'CHATBOT_ENGAGED',
  'CALL_BOOKED',
  'CUSTOMER',
  'COLD',
]);
export type LeadStatus = z.infer<typeof LeadStatusSchema>;

// ─── Genel formatlar ────────────────────────────────────────────

/** Email — case-insensitive, lowercase normalize */
export const EmailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .email({ message: 'Geçerli bir e-posta adresi girin' });

/** Password kuralları — Spec Bölüm 4.5.4 */
export const PasswordSchema = z
  .string()
  .min(8, 'Şifre en az 8 karakter olmalı')
  .max(128, 'Şifre en fazla 128 karakter olabilir')
  .refine((s) => /[A-Z]/.test(s), 'En az 1 büyük harf içermeli')
  .refine((s) => /[a-z]/.test(s), 'En az 1 küçük harf içermeli')
  .refine((s) => /\d/.test(s), 'En az 1 rakam içermeli');

/** CUID id format (Prisma default) */
export const CuidSchema = z.string().regex(/^c[a-z0-9]{24}$/, 'Geçersiz ID');

/** ISO date string */
export const IsoDateStringSchema = z.string().datetime();

/** Pagination cursor */
export const PaginationSchema = z.object({
  cursor: z.string().optional(),
  limit: z.number().int().min(1).max(100).default(20),
});
export type Pagination = z.infer<typeof PaginationSchema>;
